import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { StatusBadge, PriorityBadge } from "../components/StatusBadge";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  List,
  Grid3X3,
  AlertCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Get days in a month
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// Get first day of month (0 = Sunday)
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

// Format date as YYYY-MM-DD
const formatDate = (year, month, day) => {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

// Check if date is today
const isToday = (year, month, day) => {
  const today = new Date();
  return (
    today.getFullYear() === year &&
    today.getMonth() === month &&
    today.getDate() === day
  );
};

// Check if date is in the past
const isPast = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// Project card for calendar
const ProjectCard = ({ project, compact = false }) => {
  const isOverdue = project.deadline && isPast(project.deadline) && project.status !== "approved";
  
  if (compact) {
    return (
      <Link
        to={`/projects/${project.id}`}
        className={`block text-xs p-1 rounded truncate ${
          isOverdue
            ? "bg-red-950/50 text-red-300 border border-red-800"
            : "bg-primary/20 text-primary hover:bg-primary/30"
        }`}
        title={project.name}
      >
        {project.name}
      </Link>
    );
  }

  return (
    <Link
      to={`/projects/${project.id}`}
      className={`block p-3 rounded-lg border transition-colors ${
        isOverdue
          ? "bg-red-950/30 border-red-800 hover:bg-red-950/50"
          : "bg-card border-border hover:bg-secondary/50"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-sm truncate">{project.name}</h4>
        {isOverdue && <AlertCircle size={14} className="text-red-400 flex-shrink-0" />}
      </div>
      <p className="text-xs text-muted-foreground mb-2 truncate">{project.customer_name}</p>
      <div className="flex items-center gap-2">
        <StatusBadge status={project.status} />
        <PriorityBadge priority={project.priority} />
      </div>
    </Link>
  );
};

// Calendar day cell
const DayCell = ({ year, month, day, projects, isCurrentMonth }) => {
  const dateStr = formatDate(year, month, day);
  const dayProjects = projects.filter((p) => p.deadline?.startsWith(dateStr));
  const today = isToday(year, month, day);

  return (
    <div
      className={`min-h-[100px] p-2 border-r border-b border-border ${
        !isCurrentMonth ? "bg-secondary/30" : ""
      } ${today ? "bg-primary/10" : ""}`}
    >
      <div className={`text-sm font-medium mb-1 ${
        today ? "text-primary" : !isCurrentMonth ? "text-muted-foreground" : ""
      }`}>
        {day}
        {today && <span className="ml-1 text-xs">(Today)</span>}
      </div>
      <div className="space-y-1">
        {dayProjects.slice(0, 3).map((project) => (
          <ProjectCard key={project.id} project={project} compact />
        ))}
        {dayProjects.length > 3 && (
          <div className="text-xs text-muted-foreground">+{dayProjects.length - 3} more</div>
        )}
      </div>
    </div>
  );
};

// Timeline view
const TimelineView = ({ projects }) => {
  const sortedProjects = [...projects].sort((a, b) => 
    new Date(a.deadline) - new Date(b.deadline)
  );

  const groupedByMonth = sortedProjects.reduce((acc, project) => {
    const date = new Date(project.deadline);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(project);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {Object.entries(groupedByMonth).map(([monthKey, monthProjects]) => {
        const [year, month] = monthKey.split("-");
        const monthName = MONTHS[parseInt(month) - 1];
        
        return (
          <div key={monthKey}>
            <h3 className="text-lg font-semibold mb-4 text-primary">
              {monthName} {year}
            </h3>
            <div className="space-y-3">
              {monthProjects.map((project) => {
                const deadline = new Date(project.deadline);
                const isOverdue = isPast(project.deadline) && project.status !== "approved";
                
                return (
                  <div
                    key={project.id}
                    className={`flex items-center gap-4 p-4 rounded-lg border ${
                      isOverdue
                        ? "bg-red-950/30 border-red-800"
                        : "bg-card border-border"
                    }`}
                  >
                    <div className={`text-center min-w-[50px] ${isOverdue ? "text-red-400" : ""}`}>
                      <div className="text-2xl font-bold">{deadline.getDate()}</div>
                      <div className="text-xs text-muted-foreground uppercase">
                        {DAYS[deadline.getDay()]}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-medium hover:text-primary transition-colors"
                      >
                        {project.name}
                      </Link>
                      <p className="text-sm text-muted-foreground">{project.customer_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={project.status} />
                      <PriorityBadge priority={project.priority} />
                    </div>
                    {isOverdue && (
                      <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">
                        OVERDUE
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const CalendarPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("calendar"); // "calendar" or "timeline"
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/stats/calendar`);
      setProjects(res.data.projects || []);
    } catch (err) {
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const daysInPrevMonth = getDaysInMonth(year, month - 1);
  
  const calendarDays = [];
  
  // Previous month days
  for (let i = firstDay - 1; i >= 0; i--) {
    calendarDays.push({
      day: daysInPrevMonth - i,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      isCurrentMonth: false,
    });
  }
  
  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      day: i,
      month,
      year,
      isCurrentMonth: true,
    });
  }
  
  // Next month days
  const remaining = 42 - calendarDays.length;
  for (let i = 1; i <= remaining; i++) {
    calendarDays.push({
      day: i,
      month: month + 1,
      year: month === 11 ? year + 1 : year,
      isCurrentMonth: false,
    });
  }

  // Stats
  const overdueCount = projects.filter(
    (p) => isPast(p.deadline) && p.status !== "approved"
  ).length;
  const thisMonthCount = projects.filter((p) => {
    const d = new Date(p.deadline);
    return d.getFullYear() === year && d.getMonth() === month;
  }).length;

  if (loading) {
    return <div className="animate-pulse text-muted-foreground">Loading calendar...</div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarIcon size={28} />
            Project Calendar
          </h1>
          <p className="text-muted-foreground mt-1">
            View project deadlines and manage schedules
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setView("calendar")}
            className={`p-2 rounded-lg transition-colors ${
              view === "calendar"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary hover:bg-secondary/80"
            }`}
            title="Calendar View"
          >
            <Grid3X3 size={20} />
          </button>
          <button
            onClick={() => setView("timeline")}
            className={`p-2 rounded-lg transition-colors ${
              view === "timeline"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary hover:bg-secondary/80"
            }`}
            title="Timeline View"
          >
            <List size={20} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground">Total with Deadlines</p>
          <p className="text-2xl font-bold">{projects.length}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm text-muted-foreground">This Month</p>
          <p className="text-2xl font-bold text-primary">{thisMonthCount}</p>
        </div>
        <div className={`rounded-xl p-4 border ${
          overdueCount > 0 
            ? "bg-red-950/30 border-red-800" 
            : "bg-card border-border"
        }`}>
          <p className="text-sm text-muted-foreground">Overdue</p>
          <p className={`text-2xl font-bold ${overdueCount > 0 ? "text-red-400" : ""}`}>
            {overdueCount}
          </p>
        </div>
      </div>

      {view === "calendar" ? (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <button
                onClick={prevMonth}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <ChevronRight size={20} />
              </button>
              <h2 className="text-xl font-semibold ml-2">
                {MONTHS[month]} {year}
              </h2>
            </div>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
            >
              Today
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-border">
            {DAYS.map((day) => (
              <div
                key={day}
                className="p-2 text-center text-sm font-medium text-muted-foreground border-r border-border last:border-r-0"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {calendarDays.map((dayInfo, idx) => (
              <DayCell
                key={idx}
                year={dayInfo.year}
                month={dayInfo.month}
                day={dayInfo.day}
                projects={projects}
                isCurrentMonth={dayInfo.isCurrentMonth}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-6">
          {projects.length > 0 ? (
            <TimelineView projects={projects} />
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p>No projects with deadlines</p>
              <p className="text-sm mt-1">Set deadlines on projects to see them here</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
